import { useCallback, useEffect, useState, type ReactNode } from "react";

import { money } from "../money.ts";
import type { CartLine, CartReview, Merchant, RiderApi } from "../rider-api.ts";
import { SCOPES } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import {
  ConflictCard,
  Group,
  MarketShell,
  Stepper,
  Totals,
  type BadgeValue,
  type MarketAction,
  type RowValue,
  type TotalValue,
} from "./MarketShell.tsx";
import { Absent } from "./PhoneShell.tsx";
import { MarketState } from "./TrancheState.tsx";

/**
 * `Cart` and `Cart conflict` — one read, two screens, and a 409 decides which.
 *
 * The same shape as `3 Quote` and `3b Quote expired`: the design draws two states of one moment, and
 * the platform publishes exactly that. `PUT /v1/carts/{cartId}/items` takes an `expectedVersion` and
 * answers **409** when the cart has moved on since this phone last read it — which is the design's
 * own sentence arriving as a contract: _"COM-04 carts carry a version and expectedVersion on every
 * change. When the tablet changed it first, the phone shows both changes and never silently drops
 * either."_
 *
 * **So the screen follows the 409, not the address.** A rider who opens `?page=cart-conflict` with no
 * conflict in hand is shown `Cart` and the address is corrected, because this build will not draw a
 * conflict that has not happened. The conflict is reached by pressing the stepper against a cart
 * somebody else has changed, which is also how the acceptance check drives it.
 *
 * ## What it draws, and what it cannot
 *
 * | Drawn                                   | From                                                |
 * | --------------------------------------- | --------------------------------------------------- |
 * | the three lines, named and priced       | EXP-002's `cart-reviews`: COM-04 + COM-02 per line  |
 * | `may be substituted` / `store will confirm` | EXP-002's `inStock`, which is **three**-valued   |
 * | `v4 · saved`                            | COM-04's `version` and `status`                     |
 * | `Held for 25 minutes`                   | COM-04's `expiresAt`                                |
 * | `Subtotal KES 440`                      | EXP-002's `itemsTotal` — the sum of the lines       |
 * | `Delivery KES 99` / `Service KES 20`    | **nothing** — `chargeableTotalAbsence` says so      |
 * | `Total KES 559`                         | **nothing** — it is the two fees plus the subtotal  |
 * | `added on your tablet`                  | **nothing** — `itemProvenanceAbsence` says so       |
 *
 * EXP-002's own description is the clearest statement of the first gap: _"Two drawn figures have no
 * source: the delivery and service fees, so the only total published is `itemsTotal` — explicitly
 * the sum of the lines and not what the customer will be charged."_ So the three totals rows below
 * `Subtotal` are drawn in their designed order, each carrying the dashed mark, and the footer's
 * `Checkout · KES 559` loses its figure as well as its destination.
 *
 * ## The design's own arithmetic does not add up, and it is copied anyway
 *
 * `title:'3 items · KES 700'` against `rows:[['Subtotal','KES 440'],…,['Total','KES 559']]`, with
 * three lines of 360, 60 and 20. 360 + 60 + 20 is 440 and 440 + 99 + 20 is 559; there is no reading
 * on which the title's 700 is either of them. The build draws `itemCount` and `itemsTotal`, which is
 * the sentence the design's own subtotal supports, and the 700 is recorded as a design question
 * rather than reproduced — it is the one figure in the file that contradicts another figure in the
 * same file.
 */

export interface CartProps {
  readonly api: RiderApi;
  readonly scopes: ReadonlySet<string>;
  readonly tenantId: string;
  readonly customerId: string;
  readonly cartId: string | undefined;
  /** `cart` or `cart-conflict`, which is what the address asked for. */
  readonly asked: "cart" | "cart-conflict";
  /**
   * Where `Checkout · KES 559` goes, now that it goes somewhere.
   *
   * This button carried `unavailable` for as long as this screen has existed, and the sentence
   * it carried was true: COM-05 published a request body referencing a schema its contract never
   * defined. That reference resolves now, so the sentence has stopped being true — and a control
   * that still refused with a reason that no longer holds is worse than one that never worked,
   * because it reports a gap the platform has closed.
   */
  readonly onCheckout: () => void;
  /** Corrects the address when the 409 disagrees with it, in either direction. */
  readonly onScreenChange: (pageId: string) => void;
  readonly onBack: () => void;
}

/** The design's glyph for a cart line, for the reason `Products · stock` gives for the same tile. */
const LINE_GLYPH = "package_2";

/**
 * The key for one change to one line.
 *
 * Derived rather than minted, which is `booking.ts`'s reasoning applied to a cart: a rider pressing
 * `+` on a flaky connection, or this screen re-sending the same change after a reload, must ask for
 * **the same** change rather than a second one. The quantity is in the key because `6` and `7` are
 * different changes; the expected version is in it because the same quantity against a different
 * cart is a different change too.
 *
 * Document 06 refuses a key reused with materially different terms rather than answering from the
 * first request, which is what makes the derivation safe rather than clever.
 */
function changeKey(input: {
  readonly cartId: string;
  readonly productId: string;
  readonly quantity: number;
  readonly expectedVersion: number;
}): string {
  return [
    "cart-item",
    input.cartId,
    input.productId,
    String(input.quantity),
    `v${String(input.expectedVersion)}`,
  ].join(":");
}

/** What the phone knows about a conflict, once it has had one. */
interface Conflict {
  /** The version this phone was holding when it tried to write. */
  readonly held: number;
  /** The version the server had, read back after the refusal. */
  readonly server: number | null;
}

export function Cart(props: CartProps): ReactNode {
  const { api, tenantId, customerId, cartId, asked, onScreenChange } = props;

  const [review, setReview] = useState<CartReview | undefined>(undefined);
  const [merchant, setMerchant] = useState<Merchant | undefined>(undefined);
  const [state, setState] = useState<ViewState>({ kind: "loading" });
  const [conflict, setConflict] = useState<Conflict | undefined>(undefined);
  const [changing, setChanging] = useState(false);

  const load = useCallback(() => {
    if (cartId === undefined) return;
    setState({ kind: "loading" });
    api
      .cartReview({ tenantId, customerId, cartId })
      .then((answer) => {
        setReview(answer);
        setState({ kind: "ready" });
      })
      .catch((error: unknown) => setState(stateForError(error, [SCOPES.journey])));
  }, [api, tenantId, customerId, cartId]);

  useEffect(load, [load]);

  /*
   * The merchant's trading name, which the cart review does not carry.
   *
   * EXP-002 publishes `merchantId` and composes COM-02 and COM-03 per line; it does not read MER-01.
   * So the app bar's second line is one more request, failed softly — a cart whose shop could not be
   * named is still a cart, and that is a different failure from a cart that could not be read.
   */
  useEffect(() => {
    const merchantId = review?.merchantId;
    if (merchantId === null || merchantId === undefined) return;
    let current = true;
    api
      .merchant(merchantId)
      .then((answer) => {
        if (current) setMerchant(answer);
      })
      .catch(() => {
        if (current) setMerchant(undefined);
      });
    return (): void => {
      current = false;
    };
  }, [api, review?.merchantId]);

  /* The address follows the 409, in both directions. See this file's header. */
  const shouldBe = conflict === undefined ? "cart" : "cart-conflict";
  useEffect(() => {
    if (asked !== shouldBe) onScreenChange(shouldBe);
  }, [asked, shouldBe, onScreenChange]);

  const change = useCallback(
    (line: CartLine, quantity: number) => {
      if (cartId === undefined || review?.version === null || review?.version === undefined) return;
      if (quantity < 0) return;
      const expectedVersion = review.version;
      setChanging(true);
      api
        .changeCartLine(
          {
            cartId,
            productId: line.productId,
            merchantLocationId: line.merchantLocationId,
            quantity,
            unitAmountMinor: line.unitPrice.amountMinor,
            currency: line.unitPrice.currency,
            expectedVersion,
          },
          changeKey({ cartId, productId: line.productId, quantity, expectedVersion }),
        )
        .then(() => {
          setChanging(false);
          load();
        })
        .catch((error: unknown) => {
          setChanging(false);
          const failure = stateForError(error, [SCOPES.cart]);
          if (failure.kind === "error" && failure.conflict) {
            /*
             * The designed 409, and the one place in this application where a conflict is a screen
             * rather than an apology.
             *
             * The version this phone held is recorded before the re-read, because after it the
             * server's version is all there is and `v4 → v5` needs both halves. Then the cart is
             * read again — COM-04 keeps both changes, so the re-read **is** the merged cart, which
             * is what makes `Both devices' changes are shown.` true rather than reassuring.
             */
            setConflict({ held: expectedVersion, server: null });
            load();
            return;
          }
          setState(failure);
        });
    },
    [api, cartId, review?.version, load],
  );

  /* Once the merged cart has arrived, the conflict learns which version the server was on. */
  useEffect(() => {
    if (conflict === undefined || conflict.server !== null) return;
    const version = review?.version;
    if (version === null || version === undefined || version === conflict.held) return;
    setConflict({ held: conflict.held, server: version });
  }, [conflict, review?.version]);

  if (cartId === undefined) {
    return (
      <MarketState
        state={{
          kind: "error",
          code: "no_cart_in_address",
          message:
            "This screen is about one cart and the address does not name one. COM-04 publishes no listing of a customer's carts, so nothing can find it.",
          retryable: false,
          conflict: false,
        }}
        onBack={props.onBack}
        purpose="Reading your cart."
        barTitle="Your cart"
        barSub="CityCab"
      />
    );
  }

  if (state.kind !== "ready" || review === undefined) {
    return (
      <MarketState
        state={state}
        onRetry={load}
        onBack={props.onBack}
        purpose="Reading your cart."
        barTitle="Your cart"
        barSub="CityCab"
      />
    );
  }

  const isConflict = conflict !== undefined;
  const currency = review.itemsTotal?.currency ?? review.lines[0]?.unitPrice.currency ?? "";

  const rows: RowValue[] = review.lines.map((line) => ({
    id: line.productId,
    lead: LINE_GLYPH,
    title: line.name ?? "This item",
    ...(line.name === null
      ? { subAbsent: `No name: COM-02 answered ${line.productAbsence ?? "nothing"}` }
      : { sub: `${money(line.unitPrice)} each` }),
    amount: money(line.linePrice),
    badge: lineBadge(line),
    control: (
      <Stepper
        quantity={line.quantity}
        onChange={(next) => change(line, next)}
        {...(changing ? { unavailable: "A change to this cart is in flight" } : {})}
      />
    ),
  }));

  const totals: TotalValue[] = [
    {
      label: "Subtotal",
      ...(review.itemsTotal === null
        ? { absent: "No subtotal: the lines are in more than one currency" }
        : { value: money(review.itemsTotal) }),
    },
    { label: "Delivery", absent: "No delivery fee is published" },
    { label: "Service", absent: "No service fee is published" },
    {
      label: "Total",
      absent: `No chargeable total: ${review.chargeableTotalAbsence ?? "no_published_route"}`,
    },
  ];

  const actions: readonly MarketAction[] = isConflict
    ? [
        {
          label: "Keep both and continue",
          glyph: "check",
          primary: true,
          /*
           * Accepting the merged cart, which is a real act and not a placebo.
           *
           * COM-04 kept both changes when it refused this phone's write, so the cart now on screen
           * **is** both devices' changes. Pressing this says "yes, that one" — the held version
           * becomes the server's and the screen goes back to `Cart`. Nothing is written, because
           * there is nothing to write: the rider is agreeing with a state that already exists.
           */
          onPress: () => setConflict(undefined),
        },
        {
          label: "Remove tablet changes",
          glyph: "undo",
          primary: false,
          unavailable:
            "Nothing publishes which device added a line (itemProvenance), so this cart cannot say which changes were the tablet's",
        },
      ]
    : [
        {
          /*
           * The figure is still dropped, and that is a different gap from the one that is closed.
           *
           * `Checkout · KES 559` loses its amount because `chargeableTotalAbsence` says nothing
           * publishes the delivery or the service fee — unchanged. What changed is the
           * destination: the order command has a published body, so the button leads to
           * `Checkout` instead of explaining why it cannot.
           */
          label: "Checkout",
          glyph: "arrow_forward",
          primary: true,
          onPress: props.onCheckout,
        },
      ];

  return (
    <MarketShell
      screen={isConflict ? "cart-conflict" : "cart"}
      left={{ glyph: "arrow_back", label: "Back", onPress: props.onBack }}
      barTitle="Your cart"
      barSub={
        merchant === undefined ? (
          <Absent words="No shop name: the cart review publishes merchantId and not a name" />
        ) : (
          merchant.tradingName
        )
      }
      right={{
        glyph: "more_horiz",
        label: "More",
        unavailable: "No design file draws what is behind this control",
      }}
      badge={cartBadge(review, conflict)}
      title={isConflict ? "Review your cart" : itemsTitle(review, currency)}
      sub={isConflict ? "Both devices' changes are shown." : heldFor(review)}
      actions={actions}
    >
      {isConflict ? (
        <ConflictCard
          headline="Your cart changed on another device"
          detailAbsent="Nothing publishes which device changed what, or when. Both changes are kept below; nothing was lost."
        />
      ) : null}

      <Group rows={rows} empty={<EmptyCart />} />

      {isConflict ? null : <Totals rows={totals} />}
    </MarketShell>
  );
}

/** `v4 · saved` on `Cart`, `changed elsewhere · v4 → v5` on `Cart conflict`. */
function cartBadge(review: CartReview, conflict: Conflict | undefined): BadgeValue {
  if (conflict !== undefined) {
    const server = conflict.server === null ? "?" : String(conflict.server);
    return {
      kind: "human",
      label: `changed elsewhere · v${String(conflict.held)} → v${server}`,
    };
  }
  if (review.version === null) return { kind: "unknown", label: "version not read" };
  /*
   * The design's second word is `saved`, which is not a value COM-04 publishes: its `status` is
   * `active | expired`. The published word is drawn.
   */
  return {
    kind: review.expired ? "caution" : "done",
    label: `v${String(review.version)} · ${review.status ?? "status not read"}`,
  };
}

/** `3 items · KES 700` in the design; `3 items · KES 440` here. See this file's header. */
function itemsTitle(review: CartReview, currency: string): string {
  const items = `${String(review.itemCount)} ${review.itemCount === 1 ? "item" : "items"}`;
  if (review.itemsTotal === null) return items;
  return `${items} · ${money({ currency, amountMinor: review.itemsTotal.amountMinor })}`;
}

/** `Held for 25 minutes`, from COM-04's own `expiresAt`. */
function heldFor(review: CartReview): ReactNode {
  if (review.expiresAt === null) {
    return <Absent words="No hold: COM-04 did not answer an expiry for this cart" />;
  }
  if (review.expired) return "This cart has expired.";
  const minutes = Math.max(0, Math.round((Date.parse(review.expiresAt) - Date.now()) / 60_000));
  return `Held for ${String(minutes)} ${minutes === 1 ? "minute" : "minutes"}`;
}

/**
 * The line's stock badge, from EXP-002's **three**-valued `inStock`.
 *
 * `null` is the third value and is the one that earns the component's keep: it means nothing was
 * asked of COM-03 or COM-03 did not answer, and `stockAbsence` says which. `no_sku` is the sharpest
 * of its six reasons — COM-02 did not answer the SKU COM-03 is addressed by, so the question could
 * not even be put.
 */
function lineBadge(line: CartLine): BadgeValue {
  if (line.inStock === null) {
    return {
      kind: "unknown",
      label: `store will confirm · ${line.stockAbsence ?? "not answered"}`,
    };
  }
  if (!line.inStock) return { kind: "closed", label: "out of stock" };
  if (line.availableQuantity !== null && line.availableQuantity < line.quantity) {
    return { kind: "caution", label: "may be substituted" };
  }
  return { kind: "done", label: "in stock" };
}

/** The empty cart, in this build's own words: the design draws no empty state for this screen. */
function EmptyCart(): ReactNode {
  return (
    <div className="row">
      <span className="row__middle">
        <span className="row__title">Your cart is empty</span>
        <span className="row__sub">Nothing has been added to it yet.</span>
      </span>
    </div>
  );
}
